import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { CoreModule } from '@core/core.module';
import { SharedModule } from '@shared/shared.module';
import { NotesModule } from '@notes/notes.module';
import { ProjectRoutingModule } from './project-routing.module';
import { IConfig, NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { RouterModule } from '@angular/router';
import { CarrierFullProfileModule } from '../carrier-full-profile/carrier-full-profile.module';
import { UploadCsvModule } from '../upload-csv/upload-csv.module';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';

import { ProjectDetailsComponent } from './project-details/project-details.component';

import { ProjectsIconComponent } from './projects-icon/projects-icon.component';
import { ProjectActionsComponent } from './project-actions/project-actions.component';
import { RecommendedCarriersComponent } from './recommended-carriers/recommended-carriers.component';

import { PannelHeaderComponent } from './capacity-search-panel/capacity-search-pannel/pannel-header/pannel-header.component';
import { PannelWrapperComponent } from './capacity-search-panel/capacity-search-pannel/pannel-wrapper/pannel-wrapper.component';
import { PannelBodyComponent } from './capacity-search-panel/capacity-search-pannel/pannel-body/pannel-body.component';
import {
	CarrierDataFeedbackComponent,
	CarrierDataFeedbackDialog,
} from './capacity-search-panel/carrier-data-feedback/carrier-data-feedback.component';
import { CapacitySearchFilterComponent } from './capacity-search-panel/capacity-search-filter/capacity-search-filter.component';

import { CoreTasksModule } from '../broker/tasks/core-tasks/core-tasks.module';
import { CarrierFeedbackModule } from '../carrier-feedback/carrier-feedback.module';
import { ContactDetailsModule } from '../contact-details/contact-details.module';
import { LastBidDialogComponent } from './last-bid-dialog/last-bid-dialog.component';
import { MaxBuyDialogComponent } from './max-buy-dialog/max-buy-dialog.component';

import { LoadFormModule } from '../load-form/load-form.module';
import { ProjectSpecificationsComponent } from './project-details/project-specifications/project-specifications.component';
import { ProjectSpecificationsV2Component } from './project-details/project-specifications-v2/project-specifications-v2.component';
import { ProcuredCarriersComponent } from './procured-carriers/procured-carriers.component';
import { AllocatedCarriersComponent } from './allocated-carriers/allocated-carriers.component';

import { NgDragDropModule } from 'ng-drag-drop';
import { QuillModule } from 'ngx-quill';
import { MtxPopoverModule } from '@ng-matero/extensions/popover';
import { StickybitsModule } from 'ngx-stickybits';

import { PushModule } from '@ngrx/component';

import { StoreModule } from '@ngrx/store';
import { reducers } from './store/reducers';
import { EffectsModule } from '@ngrx/effects';
import { ProjectDetailsEffects } from './store/effects/project-details.effects';
import { RecommendedCarriersEffects } from './store/effects/recommended-carriers.effects';
import { ProjectCarrierMapEffects } from './store/effects/project-carrier-map.effects';
import { ProjectGroupEffects } from './store/effects/project-group.effects';
import { projectDetailsPageStateKey } from './store/state';
import { CarriersDialogModule } from '../shared/carriers-dialog/carriers-dialog.module';
import { ProjectGroupDialogComponent } from './project-group-dialog/project-group-dialog.component';
import { ProjectGroupsComponent } from './project-groups/project-groups.component';
import { AddToGroupComponent } from './add-to-group/add-to-group.component';
import { PricingHelperService } from '@service/pricing-helper/pricing-helper.service';
import { SelectProjectGroupComponent } from './select-project-group/select-project-group.component';
import { ProjectCarrierContactDialogComponent } from './project-carrier-contact-dialog/project-carrier-contact-dialog.component';
import { AllocationDecisionDialogComponent } from './allocation-decision-dialog/allocation-decision-dialog.component';
import { AllocationConfigDialogComponent } from './allocation-config-dialog/allocation-config-dialog.component';
import { LaneEditDialogComponent } from './lane-edit-dialog/lane-edit-dialog.component';
import { RibbonMenuComponent } from './project-details/ribbon-menu/ribbon-menu.component';
import { RibbonTrayComponent } from './project-details/ribbon-tray/ribbon-tray.component';
import { ProjectDetailsPanelComponent } from './project-details/project-panel/project-panel.component';

const maskConfig: Partial<IConfig> = {};

const components = [
	ProjectDetailsComponent,
	ProjectsIconComponent,
	RecommendedCarriersComponent,
	PannelHeaderComponent,
	PannelWrapperComponent,
	PannelBodyComponent,
	CapacitySearchFilterComponent,
	CarrierDataFeedbackComponent,
	CarrierDataFeedbackDialog,
	ProjectActionsComponent,
	LastBidDialogComponent,
	MaxBuyDialogComponent,
	ProjectSpecificationsComponent,
	ProjectSpecificationsV2Component,
	ProcuredCarriersComponent,
	AllocatedCarriersComponent,
];

@NgModule({
	declarations: [
		components,
		// CN-8587: EngageCarriersDialogComponent moved to CarriersDialogModule
		// so the Reports → Recommended Carriers page can also open it.
		ProjectGroupDialogComponent,
		ProjectGroupsComponent,
		AddToGroupComponent,
		SelectProjectGroupComponent,
		ProjectCarrierContactDialogComponent,
		AllocationDecisionDialogComponent,
		AllocationConfigDialogComponent,
		LaneEditDialogComponent,
		RibbonMenuComponent,
		RibbonTrayComponent,
		ProjectDetailsPanelComponent,
	],
	imports: [
		CommonModule,
		CoreModule,
		SharedModule,
		CarriersDialogModule,
		ProjectRoutingModule,
		NotesModule,
		NgxMaterialTimepickerModule,
		RouterModule,
		CarrierFullProfileModule,
		UploadCsvModule,
		MatTabsModule,
		MatDialogModule,
		MatAutocompleteModule,
		CoreTasksModule,
		CarrierFeedbackModule,
		ContactDetailsModule,
		LoadFormModule,
		NgDragDropModule.forRoot(),
		QuillModule,
		MtxPopoverModule,
		StickybitsModule,
		StoreModule.forFeature(projectDetailsPageStateKey, reducers),
		EffectsModule.forFeature([
			ProjectDetailsEffects,
			RecommendedCarriersEffects,
			ProjectCarrierMapEffects,
			ProjectGroupEffects,
		]),
		NgxMaskDirective,
		NgxMaskPipe,
		PushModule,
		ScrollingModule,
	],
	exports: [...components],
	providers: [PricingHelperService, provideEnvironmentNgxMask(maskConfig)],
})
export class ProjectModule {}
